import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  List,
  ListItem,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SignaturePad from "react-signature-canvas";
import jsPDF from "jspdf";
import { uploadDocumentToCloudinary } from "../../services/cloudinary.service";

type AgreementSignatureFields = {
  blhName: string;
  blhTitle: string;
  blhCompany: string;
  blhDate: string;
  receivingName: string;
  receivingTitle: string;
  receivingCompany: string;
  receivingDate: string;
};

export type SignedAgreementPayload = {
  signature: string;
  agreementPdfPath: string;
};

const initialAgreementSignatureFields: AgreementSignatureFields = {
  blhName: "",
  blhTitle: "",
  blhCompany: "TheBLH.com LLC",
  blhDate: "",
  receivingName: "",
  receivingTitle: "",
  receivingCompany: "",
  receivingDate: "",
};

const bulletSx = {
  pt: 0,
  "& .MuiListItem-root": { display: "list-item", py: 0 },
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold", mt: 2 }}>
    {children}
  </Typography>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" paragraph sx={{ pl: 2 }}>
    {children}
  </Typography>
);

const BulletList = ({
  items,
  type = "lower-alpha",
  start,
}: {
  items: React.ReactNode[];
  type?: "disc" | "lower-alpha" | "lower-roman";
  start?: number;
}) => (
  <List
    component={type === "disc" ? "ul" : "ol"}
    dense
    start={start}
    sx={{ listStyleType: type, pl: 4, ...bulletSx }}
  >
    {items.map((item, index) => (
      <ListItem key={index}>
        <Typography component="span" variant="body2">
          {item}
        </Typography>
      </ListItem>
    ))}
  </List>
);

const SignatureTextField = ({
  label,
  value,
  onChange,
  type = "text",
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "date";
  readOnly?: boolean;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 1.25 }}>
    <Typography variant="body2" sx={{ minWidth: 72 }}>
      {label}:
    </Typography>
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      type={type}
      variant="standard"
      size="small"
      fullWidth
      InputProps={{ disableUnderline: false, readOnly }}
      inputProps={{ "aria-label": label }}
      sx={{
        "& .MuiInputBase-root": {
          fontSize: "0.875rem",
        },
      }}
    />
  </Box>
);

const SignatureLine = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 1.25 }}>
    <Typography variant="body2" sx={{ minWidth: 72 }}>
      {label}:
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

type DocumentContentProps = {
  signatureFields: AgreementSignatureFields;
  onSignatureFieldChange: (field: keyof AgreementSignatureFields, value: string) => void;
  blhSignaturePad: React.ReactNode;
  receivingSignaturePad: React.ReactNode;
};

const DocumentContent = React.forwardRef<HTMLDivElement, DocumentContentProps>(({
  signatureFields,
  onSignatureFieldChange,
  blhSignaturePad,
  receivingSignaturePad,
}, ref) => (
  <Box
    ref={ref}
    data-agreement-content="true"
    sx={{ maxHeight: "70vh", overflow: "auto", p: 2, border: "1px solid #e0eeef", borderRadius: 1, backgroundColor: "#f9fbfd" }}
  >
    <Typography variant="h6" align="center" sx={{ fontWeight: "bold", mb: 1 }}>
      WWW.THEBLH.COM
    </Typography>
    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
      30 N Gould St Ste R Sheridan, WY 82801
    </Typography>
    <Divider />
    <Typography variant="h5" align="center" sx={{ fontWeight: "bold", my: 2, color: "darkred" }}>
      CONFIDENTIALITY AND MUTUAL NON-CIRCUMVENTION AGREEMENT
    </Typography>

    <Typography variant="body2" paragraph>
      The BLH (www.theblh.com), operating through TheBLH.com LLC ("THEBLH.COM") has been retained to serve as an advisor to a privately-held business (the "Company" or "Disclosing Party") to pursue strategic alternatives for the Company (the "Transaction" or "Transactions"). Before disclosing the name and location of, and additional information about, the Company, we require a confidentiality and mutual non-circumvention agreement (the "Agreement"). In consideration of THEBLH.COM providing information on behalf of the Company that is non-public, confidential or proprietary in nature to the undersigned party (the "Recipient," together with the Disclosing Party, the "Parties", and each, a "Party"), the Recipient understands and agrees to the covenants, terms and conditions set forth herein as follows:
    </Typography>

    <SectionTitle>1. Confidential Information.</SectionTitle>
    <Paragraph>
      Except as set forth in Section 2 below, "Confidential Information" means all non-public, confidential or proprietary information disclosed before, on or after the execution date of this Agreement (the "Effective Date"), by THEBLH.COM on behalf of the Company or from the Disclosing Party to the Recipient or its affiliates, or to any of such Recipient's or its affiliates' employees, officers, directors, partners, shareholders, agents, attorneys, accountants or advisors (collectively, "Representatives"), whether disclosed orally or disclosed or accessed in written, electronic or other form or media, and whether or not marked, designated or otherwise identified as "confidential," including, without limitation:
    </Paragraph>
    <BulletList type="lower-alpha" items={[
      "all information concerning the Disclosing Party's and its affiliates', and their customers', suppliers' and other third parties' past, present and future business affairs including, without limitation, finances, property information, customer information, tenant information, supplier information, costs, services, organizational structure and internal practices, forecasts, sales and other financial results, records and budgets, and business, marketing, development, sales and other commercial strategies;",
      "the Disclosing Party's unpatented inventions, ideas, methods and discoveries, trade secrets, know-how, unpublished patent applications and other confidential intellectual property;",
      "all designs, specifications, documentation, components, images, icons, audiovisual components and objects, schematics, drawings, protocols, processes, and other visual depictions, in whole or in part, of any of the foregoing;",
      "any third-party confidential information included with, or incorporated in, any information provided by the Disclosing Party to the Recipient or its Representatives; and",
      'all notes, analyses, compilations, reports, forecasts, studies, data, statistics, summaries, interpretations and other materials (the "Notes") prepared by or for the Recipient or its Representatives that contain, are based on, or otherwise reflect or are derived from, in whole or in part, any of the foregoing.',
    ]} />

    <SectionTitle>2. Exclusions from Confidential Information.</SectionTitle>
    <Paragraph>
      Except as required by applicable federal, state or local law or regulation, the term "Confidential Information" as used in this Agreement shall not include information that:
    </Paragraph>
    <BulletList items={[
      "at the time of disclosure is, or thereafter becomes, generally available to and known by the public other than as a result of, directly or indirectly, any violation of this Agreement by the Recipient or any of its Representatives, as established by documentary evidence;",
      "at the time of disclosure is, or thereafter becomes, available to the Recipient on a non-confidential basis from a third-party source, provided that such third party is not and was not prohibited from disclosing such Confidential Information to the Recipient by a legal, fiduciary or contractual obligation to the Disclosing Party, as established by documentary evidence;",
      "was known by or in the possession of the Recipient or its Representatives, as established by documentary evidence, prior to being disclosed by or on behalf of the Disclosing Party pursuant to this Agreement; or",
      "was or is independently developed by the Recipient, as established by documentary evidence, without reference to or use of, in whole or in part, any of the Disclosing Party's Confidential Information.",
    ]} />

    <SectionTitle>3. Recipient Obligations.</SectionTitle>
    <Paragraph>The Recipient shall:</Paragraph>
    <BulletList items={[
      "protect and safeguard the confidentiality of all such Confidential Information with at least the same degree of care as the Recipient would protect its own Confidential Information, but in no event with less than a commercially reasonable degree of care;",
      "not use the Disclosing Party's Confidential Information, or permit it to be accessed or used, for any purpose other than the Purpose or any related transactions between the Parties, or otherwise in any manner to the Disclosing Party's detriment, including without limitation, to reverse engineer, disassemble, decompile or design around the Disclosing Party's proprietary services, products and/or confidential intellectual property;",
      "not disclose any such Confidential Information to any person or entity, except to the Recipient's Representatives who:",
    ]} />
    <BulletList type="lower-roman" items={[
      "need to know the Confidential Information to assist the Recipient, or act on its behalf, in relation to the Purpose or to exercise its rights under the Agreement;",
      "are informed by the Recipient of the confidential nature of the Confidential Information; and",
      "are subject to confidentiality duties or obligations to the Recipient that are no less restrictive than the terms and conditions of this Agreement;",
    ]} />
    <BulletList start={4} items={[
      "not contact the Company's owners or its employees, suppliers, customers, landlords or other business contacts, except through THEBLH.COM. All correspondence, inquiries, offers, and negotiations relating to a Transaction presented by THEBLH.COM will be conducted exclusively through THEBLH.COM;",
      "not circumvent or interfere with THEBLH.COM's agreement with the Company in any way. Recipient understands that if Recipient interferes with any rights under the terms and conditions of any agreement between THEBLH.COM and the Company, Recipient may be personally liable to THEBLH.COM for payment of compensation owed by the Company, plus damages. Recipient understands that if Recipient consummates a Transaction or Transactions through THEBLH.COM, Recipient will not be liable for compensation paid by Company to THEBLH.COM; and",
      "be responsible for any breach of this Agreement caused by any of its Representatives.",
    ]} />

    <SectionTitle>4. Additional Confidentiality Obligations.</SectionTitle>
    <Paragraph>
      Except as required by applicable federal, state or local law or regulation, the Recipient shall not, nor permit any of its Representatives to, disclose to any person:
    </Paragraph>
    <BulletList items={[
      "that the Confidential Information has been made available to it or its Representatives, or that it has inspected any portion of the Confidential Information;",
      "that discussions or negotiations may be, or are, underway between the Parties regarding the Confidential Information or the Transaction, including the status thereof;",
      "any terms, conditions or other arrangements that are being discussed or negotiated in relation to the Confidential Information or the Transaction; or",
      "the existence of this Agreement.",
    ]} />

    <SectionTitle>5. Required Disclosure.</SectionTitle>
    <Paragraph>
      Any Disclosure by the Recipient or its Representatives of any of the Disclosing Party's Confidential Information pursuant to applicable federal, state or local law, regulation or a valid order issued by a court or governmental agency of competent jurisdiction (a "Legal Order") shall be subject to the terms of this Section. Prior to making any such disclosure, the Recipient shall provide THEBLH.COM and the Disclosing Party with:
    </Paragraph>
    <BulletList items={[
      "prompt written notice of such requirement so that the Disclosing Party may seek, at its sole cost and expense, a protective order or other remedy; and",
      "reasonable assistance, at the Disclosing Party's sole cost and expense, in opposing such disclosure or seeking a protective order or other limitations on disclosure.",
    ]} />
    <Paragraph>
      If, after providing such notice and assistance as required herein, the Recipient remains subject to a Legal Order to disclose any Confidential Information, the Recipient (or its Representatives or other persons to whom such Legal Order is directed) shall disclose no more than that portion of the Confidential Information which, on the advice of the Recipient's legal counsel, such Legal Order specifically requires the Recipient to disclose and, upon the Disclosing Party's request, shall use commercially reasonable efforts to obtain assurances from the applicable court or agency that such Confidential Information will be afforded confidential treatment.
    </Paragraph>

    <SectionTitle>6. Return or Destruction of Confidential Information.</SectionTitle>
    <Paragraph>
      At any time during or after the term of this Agreement, at THEBLH.COM's or the Disclosing Party's written request, the Recipient and its Representatives shall promptly return to the Disclosing Party all copies, whether in written, electronic or other form or media, of the Disclosing Party's Confidential Information, or destroy all such copies and certify in writing to the Disclosing Party that such Confidential Information has been destroyed. In addition, the Recipient shall also destroy all copies of any Notes created by the Recipient or its Representatives and certify in writing to the Disclosing Party that such copies have been destroyed.
    </Paragraph>

    <SectionTitle>7. Mutual Non-Circumvention</SectionTitle>
    <Paragraph>
      (a) Each party irrevocably agrees that without the express written consent of the other party, it shall not, directly or indirectly, interfere with, circumvent or attempt to circumvent, avoid, bypass, contact or obviate each other party's interest, or the interest or relationship between the individual party and its pre-established relationships with customers, vendors, clients, producers, sellers, brokers, debt sellers or buyers, creditors, financial institutions, distributors, agencies, debt collectors, law firms, attorneys, investors and other partners at any time and for any purpose (collectively, "Contacts"). No party shall make contact with, solicit, deal with, or otherwise be involved in any transaction(s) with regard to any of the other party's Contacts unless such party obtains prior written authorization from the other party, and then only for such described transaction.
    </Paragraph>
    <Paragraph>
      (b) In case of circumvention or other violation of this Section, in addition to any other right or remedy otherwise available to the non-breaching party, the breaching party agrees and guarantees that it will pay an amount equal to the amount the non-breaching party should have realized in such transactions had the transactions occurred through or been facilitated by such non-breaching party.
    </Paragraph>
    <Paragraph>
      (c) Each party further agrees that the provisions of this Agreement and this Section extend to its respective Affiliates and Agents.
    </Paragraph>

    <SectionTitle>8. Term and Termination.</SectionTitle>
    <Paragraph>
      The term of this Agreement shall commence on the Effective Date and shall expire two (2) years from the Effective Date, provided that either Party may terminate this Agreement at any time by providing written notice to the other Party. Notwithstanding anything to the contrary herein, each Party's rights and obligations under this Agreement shall survive any expiration or termination of this Agreement indefinitely, even after the return or destruction of Confidential Information by the Recipient.
    </Paragraph>

    <SectionTitle>9. No Representations or Warranties.</SectionTitle>
    <Paragraph>
      Neither THEBLH.COM nor the Disclosing Party nor any of its Representatives make any representation or warranty, expressed or implied, as to the accuracy or completeness of the Confidential Information disclosed to the Recipient hereunder. Neither the Disclosing Party nor any of its Representatives shall be liable to the Recipient or any of its Representatives relating to or resulting from the Recipient's use of any of the Confidential Information or any errors therein or omissions therefrom. Understanding that, Recipient shall make an independent verification of said Confidential Information prior to entering into an agreement to consummate a Transaction with the Company. Recipient agrees to indemnify and hold THEBLH.COM and its affiliates and agents harmless from any claims or damages which may occur by reason of the inaccuracy or incompleteness of any Confidential Information provided to Recipient with respect to any potential Transaction.
    </Paragraph>

    <SectionTitle>10. No Transfer of Rights, Title or Interest.</SectionTitle>
    <Paragraph>
      The Disclosing Party hereby retains its entire right, title and interest, including all intellectual property rights, in and to all of its Confidential Information. Any disclosure of such Confidential Information hereunder shall not be construed as an assignment, grant, option, license or other transfer of any such right, title or interest whatsoever to the Recipient or any of its Representatives.
    </Paragraph>

    <SectionTitle>11. No Other Obligation.</SectionTitle>
    <Paragraph>
      The Parties agree that neither Party shall be under any legal obligation of any kind whatsoever, or otherwise be obligated to enter into any business or contractual relationship, investment, or Transaction, by virtue of this Agreement, except for the matters specifically agreed to herein. Either Party may at any time, at its sole discretion with or without cause, terminate discussions and negotiations with the other Party, in connection with the Purpose or otherwise.
    </Paragraph>

    <SectionTitle>12. Remedies.</SectionTitle>
    <Paragraph>
      Each Party acknowledges and agrees that money damages might not be a sufficient remedy for any breach or threatened breach of this Agreement by the Recipient or its Representatives. Therefore, in addition to all other remedies available at law (which neither Party waives by the exercise of any rights hereunder), the Disclosing Party shall be entitled to seek specific performance and injunctive and other equitable relief as a remedy for any such breach or threatened breach, and the Parties hereby waive any requirement for the securing or posting of any bond or the showing of actual monetary damages in connection with such claim.
    </Paragraph>

    <SectionTitle>13. Governing Law, Jurisdiction and Venue.</SectionTitle>
    <Paragraph>
      This Agreement shall be governed by and construed in accordance with the internal laws of the State of Illinois without giving effect to any choice or conflict of law provision or rule (whether of the State of Illinois or any other jurisdiction) that would cause the application of Laws of any jurisdiction other than those of the State of Illinois. Any legal suit, action or proceeding arising out of or related to this Agreement or the matters contemplated hereunder shall be instituted exclusively in the federal courts of the United States or the courts of the State of Illinois in each case located in the State of Illinois, and each Party irrevocably submits to the exclusive jurisdiction of such courts in any such suit, action or proceeding and waives any objection based on improper venue or forum non conveniens. Service of process, summons, notice or other document by mail to such Party's address set forth herein shall be effective service of process for any suit, action or other proceeding brought in any such court.
    </Paragraph>

    <SectionTitle>14. Notices.</SectionTitle>
    <Paragraph>
      All notices, requests, consents, claims, demands, waivers and other communications hereunder shall be in writing and shall be deemed to have been given: (a) when delivered by hand (with written confirmation of receipt); (b) when received by the addressee if sent by a nationally recognized overnight courier (receipt requested); (c) on the date sent by facsimile or e-mail of a PDF document (with confirmation of transmission) if sent during normal business hours of the recipient, and on the next business day if sent after normal business hours of the recipient; or (d) on the third day after the date mailed, by certified or registered mail, return receipt requested, postage prepaid. Such communications must be sent to the respective parties at the addresses set forth on the first page of this Agreement (or to such other address that may be designated by a Party from time to time in accordance with this Section).
    </Paragraph>

    <SectionTitle>15. Entire Agreement.</SectionTitle>
    <Paragraph>
      This Agreement constitutes the sole and entire agreement of the Parties with respect to the subject matter contained herein, and supersedes all prior and contemporaneous understandings, agreements, representations and warranties, both written and oral, with respect to such subject matter. This Agreement may only be amended, modified or supplemented by an agreement in writing signed by each Party hereto.
    </Paragraph>

    <SectionTitle>16. Severability.</SectionTitle>
    <Paragraph>
      If any term or provision of this Agreement is invalid, illegal or unenforceable in any jurisdiction, such invalidity, illegality or unenforceability shall not affect any other term or provision of this Agreement or invalidate or render unenforceable such term or provision in any other jurisdiction.
    </Paragraph>

    <SectionTitle>17. Counterparts.</SectionTitle>
    <Paragraph>
      This Agreement may be executed in counterparts, each of which shall be deemed an original, but all of which together shall be deemed to be one and the same agreement. A signed copy of this Agreement delivered by facsimile, e-mail or other means of electronic transmission shall be deemed to have the same legal effect as delivery of an original signed copy of this Agreement.
    </Paragraph>

    <SectionTitle>18. Assignment.</SectionTitle>
    <Paragraph>
      Neither THEBLH.COM nor either Party may assign any of its rights or delegate any of its obligations hereunder without the prior written consent of the other Party. Any purported assignment or delegation in violation of this Section shall be null and void. No assignment or delegation shall relieve the assigning or delegating Party of any of its obligations hereunder. This Agreement is for the sole benefit of the parties hereto and their respective successors and permitted assigns and nothing herein, express or implied, is intended to or shall confer upon any other person or entity any legal or equitable right, benefit or remedy of any nature whatsoever under or by reason of this Agreement.
    </Paragraph>

    <SectionTitle>19. Waivers.</SectionTitle>
    <Paragraph>
      No waiver by any Party of any of the provisions hereof shall be effective unless explicitly set forth in writing and signed by the Party so waiving. No waiver by any Party shall operate or be construed as a waiver in respect of any failure, breach or default not expressly identified by such written waiver, whether of a similar or different character, and whether occurring before or after that waiver. No failure to exercise, or delay in exercising, any right, remedy, power or privilege arising from this Agreement shall operate or be construed as a waiver thereof; nor shall any single or partial exercise of any right, remedy, power or privilege hereunder preclude any other or further exercise thereof or the exercise of any other right, remedy, power or privilege.
    </Paragraph>

    <Typography variant="body2" align="center" sx={{ mt: 3, mb: 2, fontStyle: "italic", color: "primary.main" }}>
      IN WITNESS WHEREOF, the parties have executed this Agreement to be effective as of the Effective Date.
    </Typography>

    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid size={{xs:12, md:6}}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          THE BLH.com:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          www.theblh.com
        </Typography>
        <SignatureLine label="Signature">{blhSignaturePad}</SignatureLine>
        <SignatureTextField
          label="Name"
          value={signatureFields.blhName}
          onChange={(value) => onSignatureFieldChange("blhName", value)}
        />
        <SignatureTextField
          label="Title"
          value={signatureFields.blhTitle}
          onChange={(value) => onSignatureFieldChange("blhTitle", value)}
        />
        <SignatureTextField
          label="Company"
          value={signatureFields.blhCompany}
          onChange={(value) => onSignatureFieldChange("blhCompany", value)}
          readOnly
        />
        <SignatureTextField
          label="Date"
          type="date"
          value={signatureFields.blhDate}
          onChange={(value) => onSignatureFieldChange("blhDate", value)}
        />
      </Grid>
      <Grid size={{xs:12, md:6}}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          RECEIVING PARTY:
        </Typography>
        <SignatureLine label="Signature">{receivingSignaturePad}</SignatureLine>
        <SignatureTextField
          label="Name"
          value={signatureFields.receivingName}
          onChange={(value) => onSignatureFieldChange("receivingName", value)}
        />
        <SignatureTextField
          label="Title"
          value={signatureFields.receivingTitle}
          onChange={(value) => onSignatureFieldChange("receivingTitle", value)}
        />
        <SignatureTextField
          label="Company"
          value={signatureFields.receivingCompany}
          onChange={(value) => onSignatureFieldChange("receivingCompany", value)}
        />
        <SignatureTextField
          label="Date"
          type="date"
          value={signatureFields.receivingDate}
          onChange={(value) => onSignatureFieldChange("receivingDate", value)}
        />
      </Grid>
    </Grid>
  </Box>
));

const DigitalSignModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSigned: (payload: SignedAgreementPayload) => void | Promise<void>;
  onLogout: () => void;
}> = ({ open, onClose, onSigned, onLogout }) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const blhSigPad = useRef<any>(null);

  const receivingSigPad = useRef<any>(null);
  const blhContainerRef = useRef<HTMLDivElement>(null);
  const receivingContainerRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [signatureFields, setSignatureFields] = useState<AgreementSignatureFields>(
    initialAgreementSignatureFields
  );
  const [blhCanvasSize, setBlhCanvasSize] = useState({ width: 400, height: 96 });
  const [receivingCanvasSize, setReceivingCanvasSize] = useState({ width: 400, height: 96 });

  const handleSignatureFieldChange = (
    field: keyof AgreementSignatureFields,
    value: string
  ) => {
    setSignatureFields((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const updateSize = useCallback(() => {
    const getHeight = (width: number) => (width > 480 ? 120 : 96);

    if (blhContainerRef.current) {
      const width = blhContainerRef.current.offsetWidth;
      setBlhCanvasSize({ width, height: getHeight(width) });
    }

    if (receivingContainerRef.current) {
      const width = receivingContainerRef.current.offsetWidth;
      setReceivingCanvasSize({ width, height: getHeight(width) });
    }
  }, []);

  useLayoutEffect(() => {
    if (open) {
      updateSize();
      window.addEventListener("resize", updateSize);
    }
    return () => window.removeEventListener("resize", updateSize);
  }, [open, updateSize]);

  const handleClear = () => {
    blhSigPad.current?.clear();
    receivingSigPad.current?.clear();
    setError("");
  };

  const createAgreementPdfFile = async () => {
    if (!documentRef.current) {
      throw new Error("Agreement content not found.");
    }

    const pdf = new jsPDF("p", "mm", "a4", true);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;
    let cursorY = margin;

    const ensureSpace = (height: number) => {
      if (cursorY + height > pageHeight - margin) {
        pdf.addPage();
        cursorY = margin;
      }
    };

    const addCenteredText = (text: string, size: number, isBold = false) => {
      pdf.setFont("helvetica", isBold ? "bold" : "normal");
      pdf.setFontSize(size);
      const lines = pdf.splitTextToSize(text, usableWidth);
      ensureSpace(lines.length * (size * 0.38) + 4);
      lines.forEach((line: string) => {
        pdf.text(line, pageWidth / 2, cursorY, { align: "center" });
        cursorY += size * 0.38;
      });
      cursorY += 3;
    };

    const addParagraph = (text: string, size = 9) => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(size);
      const lines = pdf.splitTextToSize(text.trim(), usableWidth);
      lines.forEach((line: string) => {
        ensureSpace(5);
        pdf.text(line, margin, cursorY);
        cursorY += 4.5;
      });
      cursorY += 2;
    };

    const addSignatureTableRow = ({
      label,
      value,
      image,
      rowHeight = image ? 24 : 9,
    }: {
      label: string;
      value?: string;
      image?: string;
      rowHeight?: number;
    }) => {
      ensureSpace(rowHeight);
      const labelWidth = 48;
      pdf.setDrawColor(160, 160, 160);
      pdf.rect(margin, cursorY, usableWidth, rowHeight);
      pdf.line(margin + labelWidth, cursorY, margin + labelWidth, cursorY + rowHeight);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text(label, margin + 3, cursorY + 6);
      pdf.setFont("helvetica", "normal");

      if (image) {
        pdf.addImage(image, "PNG", margin + labelWidth + 3, cursorY + 3, 55, rowHeight - 6);
      } else {
        const valueLines = pdf.splitTextToSize(value || "", usableWidth - labelWidth - 6);
        pdf.text(valueLines, margin + labelWidth + 3, cursorY + 6);
      }

      cursorY += rowHeight;
    };

    const agreementText = documentRef.current.innerText
      .split("THE BLH.com:")[0]
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    const sections = agreementText
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    addCenteredText("WWW.THEBLH.COM", 12, true);
    addCenteredText("30 N Gould St Ste R Sheridan, WY 82801", 9);
    addCenteredText("CONFIDENTIALITY AND MUTUAL NON-CIRCUMVENTION AGREEMENT", 12, true);

    sections
      .filter((line) => ![
        "WWW.THEBLH.COM",
        "30 N Gould St Ste R Sheridan, WY 82801",
        "CONFIDENTIALITY AND MUTUAL NON-CIRCUMVENTION AGREEMENT",
      ].includes(line))
      .forEach((line) => addParagraph(line));

    ensureSpace(12);
    cursorY += 2;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Signature Details", margin, cursorY);
    cursorY += 5;

    addSignatureTableRow({ label: "THE BLH.com", value: "www.theblh.com" });
    addSignatureTableRow({
      label: "Signature",
      image: blhSigPad.current.toDataURL("image/png"),
    });
    addSignatureTableRow({ label: "Name", value: signatureFields.blhName });
    addSignatureTableRow({ label: "Title", value: signatureFields.blhTitle });
    addSignatureTableRow({ label: "Company", value: signatureFields.blhCompany });
    addSignatureTableRow({ label: "Date", value: signatureFields.blhDate });

    cursorY += 4;
    addSignatureTableRow({ label: "Receiving Party", value: "" });
    addSignatureTableRow({
      label: "Signature",
      image: receivingSigPad.current.toDataURL("image/png"),
    });
    addSignatureTableRow({ label: "Name", value: signatureFields.receivingName });
    addSignatureTableRow({ label: "Title", value: signatureFields.receivingTitle });
    addSignatureTableRow({ label: "Company", value: signatureFields.receivingCompany });
    addSignatureTableRow({ label: "Date", value: signatureFields.receivingDate });

    return new File([pdf.output("blob")], `ncnnda-agreement-${Date.now()}.pdf`, {
      type: "application/pdf",
    });
  };

  const handleSave = async () => {
    const requiredFields: Array<[keyof AgreementSignatureFields, string]> = [
      ["receivingName", "Receiving party name"],
      ["receivingTitle", "Receiving party title"],
      ["receivingCompany", "Receiving party company"],
      ["receivingDate", "Receiving party date"],
    ];
    const missingField = requiredFields.find(([field]) => !signatureFields[field].trim());
    if (missingField) {
      setError(`${missingField[1]} is required.`);
      return;
    }
    if (blhSigPad.current?.isEmpty()) {
      setError("THE BLH.com signature is required.");
      blhContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (receivingSigPad.current?.isEmpty()) {
      setError("Digital signature is required.");
      receivingContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setError("");
    try {
      setIsGeneratingPdf(true);
      const agreementPdfFile = await createAgreementPdfFile();
      const agreementUpload = await uploadDocumentToCloudinary(
        agreementPdfFile,
        "ncnnda-agreements",
        agreementPdfFile.name
      );
      await onSigned({
        signature: receivingSigPad.current.toDataURL("image/png"),
        agreementPdfPath: agreementUpload.secureUrl,
      });
    } catch (error: any) {
      setError(error?.message || "Failed to create signed agreement PDF.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobileOrTablet}
      PaperProps={{
        sx: {
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          ...(isMobileOrTablet ? { height: "100vh", maxHeight: "100vh" } : {}),
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutlineIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Review and Sign Agreement</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        {/* 1. Full PDF Content for Review */}
        <DocumentContent
          ref={documentRef}

          signatureFields={signatureFields}
          onSignatureFieldChange={handleSignatureFieldChange}
          blhSignaturePad={
            <Box
              ref={blhContainerRef}
              sx={{
                border: "1px solid #cfd8dc",
                borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                borderRadius: 1,
                bgcolor: "#fff",
                overflow: "hidden",
                width: "100%",
                height: `${blhCanvasSize.height}px`,
              }}
            >
              <SignaturePad
                ref={blhSigPad}
                canvasProps={{
                  width: blhCanvasSize.width,
                  height: blhCanvasSize.height,
                  style: {
                    background: "#fff",
                    display: "block",
                    touchAction: "none",
                    width: "100%",
                    height: "100%",
                  },
                }}
              />
            </Box>
          }
          receivingSignaturePad={
            <Box
              ref={receivingContainerRef}
              sx={{
                border: "1px solid #cfd8dc",
                borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                borderRadius: 1,
                bgcolor: "#fff",
                overflow: "hidden",
                width: "100%",
                height: `${receivingCanvasSize.height}px`,
              }}
            >
              <SignaturePad
                ref={receivingSigPad}
                canvasProps={{
                  width: receivingCanvasSize.width,
                  height: receivingCanvasSize.height,
                  style: {
                    background: "#fff",
                    display: "block",
                    touchAction: "none",
                    width: "100%",
                    height: "100%",
                  },
                }}
              />
            </Box>
          }
        />

        <Divider sx={{ my: 3 }} />
        {error && (
          <Typography color="error" variant="caption" display="block" mb={1} sx={{ pl: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          position: isMobileOrTablet ? "sticky" : "static",
          bottom: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}

          spacing={1}
          width="100%"
          justifyContent="flex-end"
          alignItems="stretch"
        >
          <Button variant="outlined" color="error" onClick={onLogout} sx={{borderRadius: 2}}>
            Logout / Cancel
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClear}
            sx={{borderRadius: 2}}
          >
            Clear Signature
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isGeneratingPdf}
            disableElevation
            sx={{borderRadius: 2}}
          >
            {isGeneratingPdf ? "Preparing PDF..." : "I Agree & Sign Document"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DigitalSignModal;
